import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowDown, ArrowUp, Eye, GripVertical, Undo2, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VersionBadge } from "@/components/ui/documents/version-badge";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { DocumentVersioningTranslations } from "@/lib/i18n/types/documents-sections/versioning";
import { cn } from "@/lib/utils";
import type { DocumentVersion } from "../../services/versionService";
import { DocumentRowMeta } from "../atoms/DocumentRowMeta";

export interface VersioningRowProps {
  version: DocumentVersion;
  copy: DocumentVersioningTranslations["manage"];
  /** The number the row will carry after a save; null for a row leaving. */
  projectedVersion: number | null;
  /** "current" on the row that is and stays current, "becomes" on a new one. */
  currentLabel: "current" | "becomes" | null;
  separated: boolean;
  isFirst: boolean;
  isLast: boolean;
  /** Reorder and separate are off — the RPCs are missing, or a save runs. */
  editable: boolean;
  /** Why this row can't be made a separate file, or null when it can. */
  separateDisabledReason: string | null;
  /** Up (-1) or down (1) on screen, where the newest version is on top. */
  onMove: (direction: -1 | 1) => void;
  onToggleSeparate: () => void;
  /** Open this version in the viewer; absent when it cannot be previewed. */
  onPreview?: () => void;
}

/**
 * One version in the Versioning dialog. Drag it (or use the arrows) to change
 * the order; "Make separate file" marks it to leave the history on save; the
 * eye opens it in the viewer.
 */
export function VersioningRow({
  version,
  copy,
  projectedVersion,
  currentLabel,
  separated,
  isFirst,
  isLast,
  editable,
  separateDisabledReason,
  onMove,
  onToggleSeparate,
  onPreview,
}: VersioningRowProps) {
  const sortable = !separated && editable;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: version.id, disabled: !sortable });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`versioning-row-${version.id}`}
      className={cn(
        "flex items-center gap-2 rounded-md border bg-background p-2",
        separated && "border-dashed bg-muted/40",
        isDragging && "relative z-10 shadow-md"
      )}
    >
      {editable && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          disabled={!sortable}
          aria-label={copy.dragHandle}
          className="flex h-8 w-5 shrink-0 cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing disabled:cursor-default disabled:opacity-30"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}

      {projectedVersion !== null ? (
        <VersionBadge version={projectedVersion} className="w-auto min-w-6 shrink-0 px-1.5" />
      ) : (
        <span className="w-6 shrink-0" aria-hidden />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <FileTypeIcon filename={version.filename} />
          <span
            className={cn("truncate text-sm font-medium", separated && "text-muted-foreground")}
          >
            {version.filename}
          </span>
          {separated ? (
            <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              {copy.separateBadge}
            </span>
          ) : currentLabel === "current" ? (
            <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {copy.current}
            </span>
          ) : currentLabel === "becomes" ? (
            <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {copy.becomesCurrent}
            </span>
          ) : null}
        </div>
        <DocumentRowMeta
          size={version.size}
          createdAt={version.created_at}
          createdByName={version.created_by_name}
          className="mt-0.5"
        />
      </div>

      {onPreview && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0"
          aria-label={copy.preview}
          title={copy.preview}
          onClick={onPreview}
        >
          <Eye className="h-4 w-4" />
        </Button>
      )}

      {editable && (
        <div className="flex shrink-0 items-center">
          {!separated && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label={copy.moveUp}
                disabled={isFirst}
                onClick={() => onMove(-1)}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label={copy.moveDown}
                disabled={isLast}
                onClick={() => onMove(1)}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </>
          )}
          {separated ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8"
              onClick={onToggleSeparate}
            >
              <Undo2 className="mr-1.5 h-4 w-4" />
              {copy.keepAsVersion}
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* A disabled button fires no pointer events, so the tooltip
                    that explains why hangs off a wrapper. */}
                <span tabIndex={separateDisabledReason ? 0 : -1}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    aria-label={
                      separateDisabledReason
                        ? `${copy.makeSeparate}: ${separateDisabledReason}`
                        : copy.makeSeparate
                    }
                    disabled={separateDisabledReason !== null}
                    onClick={onToggleSeparate}
                  >
                    <Unlink className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {separateDisabledReason ?? (
                  <>
                    <span className="font-medium">{copy.makeSeparate}</span>
                    <br />
                    {copy.makeSeparateTooltip}
                  </>
                )}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </li>
  );
}
